import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import PageTemplate from '../../../components/common/template/PageTemplate';
import { Text } from '../../../components/common/atom/Text';
import styled from 'styled-components';
import { Button } from '../../../components/common/atom/Button';
import { ClipLoader } from 'react-spinners';

import { ERROR_RETRY } from '../../../lib/Error';
import * as HeApi from '../HeApi';
import * as PATH from '../../../lib/routes/path';
import { getCDNUrl } from '../../../lib/image/CDNUrl';

const HeReviewEvent = () => {
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplied, setIsApplied] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const { workspace } = location.state || {};

  const urlRef = useRef(null);
  const screenshotRef = useRef(null);
  const nameRef = useRef(null);
  const phoneRef = useRef(null);

  useEffect(() => {
    if (!sessionStorage.be_token) {
      alert("비정상 접근입니다. 병원에서 받은 QR 코드 또는 알림톡을 통해서 접근해 주시기 바랍니다.");
      history.push(PATH.HOME);
    }
  }, []);

  const validateFile = (file) => {
    return file && file.size <= 10 * 1024 * 1024;
  }

  const postReviewEvent = async () => {
    const urlRegex = /^(http|https):\/\/[\w\-]+(\.[\w\-]+)+[/#?]?.*$/;
    const phoneRegex = /^(010)[2-9]{1}[0-9]{7}$/;
    const url = urlRef.current.value;
    const name = nameRef.current.value;
    const phone = phoneRef.current.value.replace(/-/g, "");

    if (url === '' && !selectedFile) {
      alert("URL 또는 이미지를 입력해 주세요");
      return;
    } else if (url !== '' && !urlRegex.test(url)) {
      alert("URL 주소를 확인해 주세요");
      return;
    } else if (name.length < 2) {
      alert("이름을 확인해 주세요");
      return;
    } else if (!phoneRegex.test(phone)) {
      alert("전화번호를 확인해 주세요\n예) 01012341234");
      return;
    }
    setIsLoading(true);
    try {
      await HeApi.consentPrivacyUsage({ 
        workspaceId: workspace.id, 
        name, 
        phone, 
        token: sessionStorage.be_token
      });
    } catch {
      alert(ERROR_RETRY);
      setIsLoading(false);
      return;
    }
    try {
      const response = await HeApi.postReviewEvent({
        workspaceId: workspace.id,
        url,
        screenshot: selectedFile,
        token: sessionStorage.be_token
      });
      if (response.status === 201) {
        window.scrollTo(0, 0);
        setIsApplied(true);
      }
    } catch (error) {
      if (error.response.status === 409) {
        alert("이미 참여한 이벤트입니다.");
      }
    }
    setIsLoading(false);
  }

  const redirectToList = () => {
    history.push(`${PATH.HE_RESULTS}/${workspace.id}/list`);
  }

  return (
    <PageTemplate title={PAGE_TITLE}>
      {!isApplied ? (
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <Text size={20} weight="bold" lineHeight={1.5}>이벤트 안내<br /></Text>
            <Image src={getCDNUrl("service/event", "event_he_review.jpg")} alt="베이비페이스 후기 이벤트 이미지" />
            <Text size={20} weight="bold" lineHeight={2.5}>이벤트 신청<br /></Text>
            <FormElement>
              <Text size={16} lineHeight={1.5}>
                후기를 작성해 주셨나요?<br />
                해당 후기의 URL 주소를 남겨주세요<br />
              </Text>
              <Input type="text" ref={urlRef} placeholder="예) https://instagram.com/babyface" />
            </FormElement>
            <FormElement>
              <Text size={16} lineHeight={1.5}>URL 주소 대신 캡처 사진을 올려주셔도 좋아요</Text>
              <ImageSelector
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (validateFile(file)) {
                    setPreviewImage(URL.createObjectURL(file));
                    setSelectedFile(file);
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                <input
                  type="file"
                  accept="image/*"
                  ref={screenshotRef}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (validateFile(file)) {
                      setPreviewImage(URL.createObjectURL(file));
                      setSelectedFile(file);
                    }
                  }}
                  style={{ display: 'none' }}
                  id="imageUpload"
                />
                <label htmlFor="imageUpload" style={{ cursor: 'pointer' }}>
                  {previewImage ? (
                    <img 
                      src={previewImage} 
                      alt="Preview" 
                      style={{ maxWidth: '100%', maxHeight: '200px' }}
                    />
                  ) : (
                    <Text size={12} lineHeight={1.5}>
                      이미지를 드래그하여 놓거나 클릭하여 선택하세요<br/>
                      (JPG, PNG, GIF / 10MB 이하)
                    </Text>
                  )}
                </label>
              </ImageSelector>
            </FormElement>
            <FormElement>
              <Text size={16} lineHeight={1.5}>이름</Text>
              <Input type="text" ref={nameRef} placeholder="예) 김베페" />
            </FormElement>
            <FormElement>
              <Text size={16} lineHeight={1.5}>휴대폰</Text>
              <Input type="number" ref={phoneRef} placeholder="예) 01012341234" />
            </FormElement>
            <Text size={16} lineHeight={1.5}>영상이 완성되면, 휴대폰으로 영상 링크를 보내드려요</Text>
          </div>
          <div>
            {isLoading ? (
              <ApplyButton>
                <ClipLoader color={'#ffffff'} size={24} />
              </ApplyButton>
            ) : (
              <ApplyButton
                onClick={postReviewEvent}>
                이벤트 신청하기
              </ApplyButton>
            )}
          </div>
        </>
      ) : (
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <Text size={20} weight="bold" lineHeight={1.5}>이벤트 신청 완료!<br /></Text>
            <Text size={16} lineHeight={1.5}>
              이벤트 신청이 완료되었습니다!<br /><br />
              올려주신 소중한 후기 확인 후,<br />베이비페이스 배냇짓 영상이 완성되면 카카오 알림톡으로 보내드리겠습니다.<br />
            </Text>
            <ApplyButton
              onClick={redirectToList}>
              이미지 리스트 보기
            </ApplyButton>
          </div>
        </>
      )}
    </PageTemplate>
  )
}

export default React.memo(HeReviewEvent);

const PAGE_TITLE = '베이비페이스 후기 이벤트';

const Image = styled.img`
  display: block;
  margin: 1.5rem 0;
  width: 100%;
`;

const FormElement = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.625rem 0;
`;

const ImageSelector = styled.div`
  min-height: 6.25rem;
  border: 2px dashed #ccc;
  border-radius: 4px;
  padding: 1.7rem;
  text-align: center;
  cursor: pointer;
  margin: 0.625rem 0;
`;

const ApplyButton = styled(Button)`
  border-radius: 0.375rem;
  margin-top: 0.625rem;
  padding: 1rem;
  background: ${(props) => props.theme.color.orangePink};
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  flex: ${(props) => props.flex};
  margin: 0.625rem 0;
  padding: 0.625rem 1rem;
  border-radius: 6px;
  border: 1px solid
    ${(props) =>
      props.active
        ? props.theme.color.orangePink
        : props.theme.color.pinkishGrey};
  background: ${(props) => props.theme.background[props.background]};

  & + & {
    margin-top: 0.688rem;
  }
`;
